/**
 * PWA Install Banner Component
 * 
 * 簡約的安裝提示橫幅，顯示在 Header 上方
 * 只在移動設備且未安裝 PWA 時顯示
 */

import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  IconButton,
  Slide,
} from '@mui/material';
import {
  GetApp as GetAppIcon,
  Close as CloseIcon,
  PhoneIphone as PhoneIphoneIcon,
  Android as AndroidIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { getItem, setItem } from '../../utils/localStorage';
import {
  APP_INSTALLED_EVENT,
  INSTALL_PROMPT_AVAILABLE_EVENT,
  clearDeferredInstallPrompt,
  getDeferredInstallPrompt,
} from '../../utils/pwaInstall';

const STORAGE_KEY = 'pwaInstallBannerDismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7天

// 檢測是否為移動設備
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

// 檢測是否已安裝 PWA
const isPWAInstalled = () => {
  // 方法1: 檢測 display-mode
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // 方法2: 檢測 iOS standalone 模式
  if (window.navigator.standalone === true) {
    return true;
  }
  
  // 方法3: 檢測 Android 從主屏幕啟動
  if (document.referrer.includes('android-app://')) {
    return true;
  }
  
  return false;
};

// 檢測瀏覽器類型
const detectBrowser = () => {
  const userAgent = navigator.userAgent;
  
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  
  if (/Android/.test(userAgent)) {
    return 'android';
  }
  
  return 'other';
};

const PWAInstallBanner = () => {
  const { language } = useLanguage();
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(() => getDeferredInstallPrompt());
  const [browser, setBrowser] = useState('other');

  // 文本內容
  const texts = {
    tc: {
      message: '安裝應用程式到主屏幕，隨時快速存取',
      installButton: '安裝',
      dismissButton: '稍後',
    },
    en: {
      message: 'Install app to home screen for quick access',
      installButton: 'Install',
      dismissButton: 'Later',
    },
  };

  const t = texts[language] || texts.en;

  // 初始化檢測
  useEffect(() => {
    const checkInstallStatus = () => {
      const mobile = isMobileDevice();
      const installed = isPWAInstalled();
      const browserType = detectBrowser();
      const dismissed = getItem(STORAGE_KEY);
      
      setBrowser(browserType);

      // 如果已安裝，不顯示
      if (installed) {
        return;
      }

      // 如果用戶在7天內已經關閉過，不顯示
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        const now = Date.now();
        if (now - dismissedTime < DISMISS_DURATION) {
          return;
        }
      }

      // 只在移動設備上顯示
      if (mobile) {
        // 延遲顯示，讓用戶先看到應用內容
        const timer = setTimeout(() => {
          setShow(true);
        }, 2000); // 2秒後顯示

        return () => clearTimeout(timer);
      }
    };

    checkInstallStatus();
  }, []);

  // 同步全局 install prompt 事件
  useEffect(() => {
    const syncDeferredPrompt = () => {
      setDeferredPrompt(getDeferredInstallPrompt());
      setShow(true);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setShow(false);
    };

    // 元件掛載時先同步一次，避免錯過早期事件
    syncDeferredPrompt();

    window.addEventListener(INSTALL_PROMPT_AVAILABLE_EVENT, syncDeferredPrompt);
    window.addEventListener(APP_INSTALLED_EVENT, handleAppInstalled);

    return () => {
      window.removeEventListener(INSTALL_PROMPT_AVAILABLE_EVENT, syncDeferredPrompt);
      window.removeEventListener(APP_INSTALLED_EVENT, handleAppInstalled);
    };
  }, []);

  // 處理安裝按鈕點擊（Android Chrome）
  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        // 顯示安裝提示
        deferredPrompt.prompt();

        // 等待用戶回應
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          setShow(false);
        } else {
          console.log('User dismissed the install prompt');
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Install prompt failed, fallback to manual instructions:', error);
        }
      } finally {
        // 清除保存的提示
        clearDeferredInstallPrompt();
        setDeferredPrompt(null);
      }
    } else {
      // 如果沒有原生安裝提示，打開詳細安裝對話框
      // 可以觸發一個事件讓 PWAInstallPrompt 組件顯示
      window.dispatchEvent(new CustomEvent('pwa-install-show-dialog'));
    }
  };

  // 處理關閉
  const handleClose = () => {
    setShow(false);
    // 記錄用戶選擇稍後提醒
    setItem(STORAGE_KEY, Date.now().toString());
  };

  // 如果已安裝或不是移動設備，不顯示
  const installed = isPWAInstalled();
  const mobile = isMobileDevice();
  
  if (installed || !mobile) {
    return null;
  }

  if (!show) {
    return null;
  }

  // 計算 banner 高度
  const bannerHeight = 48;

  return (
    <>
      <Slide direction="down" in={show} mountOnEnter unmountOnExit>
        <Box>
          <AppBar
            position="fixed"
            sx={{
              top: 0,
              zIndex: 1400, // 高於 Header AppBar (通常為 1100)
              backgroundColor: 'primary.main',
              boxShadow: 2,
            }}
          >
            <Toolbar
              variant="dense"
              sx={{
                minHeight: '48px !important',
                py: 0.5,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Box display="flex" alignItems="center" gap={1} flex={1}>
                  {browser === 'ios' ? (
                    <PhoneIphoneIcon fontSize="small" />
                  ) : (
                    <AndroidIcon fontSize="small" />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                    {t.message}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleInstallClick}
                    startIcon={<GetAppIcon />}
                    sx={{
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'grey.100',
                      },
                      minWidth: 'auto',
                      px: 1.5,
                    }}
                  >
                    {t.installButton}
                  </Button>
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{
                      color: 'white',
                      p: 0.5,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Toolbar>
          </AppBar>
          {/* 添加占位元素，避免 Header 被遮擋 */}
          <Box
            sx={{
              height: `${bannerHeight}px`,
              width: '100%',
              flexShrink: 0,
            }}
          />
        </Box>
      </Slide>
    </>
  );
};

export default PWAInstallBanner;
